import { Injectable } from "@angular/core";
import { Observable, firstValueFrom } from "rxjs";

type FlowResult = "played" | "timeout" | "stopped";

export interface AlertPlaybackFlowOptions {
  eventId: string;
  waitForCoin$: Observable<void>;
  playTTS: () => Promise<void>;
  guardMs?: number;
  timeoutMs?: number;
  onTimeout?: () => void;
}

/**
 * Orquestra o fluxo de áudio de um alerta:
 * - Espera coin.wav (via waitForCoin$) ou dispara guard se não vier
 * - Executa TTS/audio de replay
 * - Aplica timeout total
 */
@Injectable({
  providedIn: "root",
})
export class AlertPlaybackOrchestrator {
  private currentEventId?: string;
  private guardTimer?: ReturnType<typeof setTimeout> | null = null;
  private timeoutTimer?: ReturnType<typeof setTimeout> | null = null;

  async runFlow(options: AlertPlaybackFlowOptions): Promise<FlowResult> {
    const guardMs = options.guardMs ?? 2000;
    const timeoutMs = options.timeoutMs ?? 30000;

    this.stop();
    this.currentEventId = options.eventId;

    const guardPromise = new Promise<FlowResult>((resolve) => {
      this.guardTimer = setTimeout(() => resolve("stopped"), guardMs);
    });

    const timeoutPromise = new Promise<FlowResult>((resolve) => {
      this.timeoutTimer = setTimeout(() => resolve("timeout"), timeoutMs);
    });

    const coinPromise = firstValueFrom(options.waitForCoin$).then(
      () => "played" as FlowResult
    );

    let result: FlowResult;
    try {
      result = await Promise.race([coinPromise, guardPromise, timeoutPromise]);

      if (this.currentEventId !== options.eventId) {
        return "stopped";
      }

      if (result === "timeout") {
        options.onTimeout?.();
        return "timeout";
      }

      // Guard disparou: ainda assim tenta tocar TTS
      await options.playTTS();
      return "played";
    } finally {
      this.stop();
    }
  }

  stop(): void {
    if (this.guardTimer) {
      clearTimeout(this.guardTimer);
      this.guardTimer = null;
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    this.currentEventId = undefined;
  }
}
